import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GlobalState } from '@leverege/ui-redux';
import { point } from '@turf/helpers';
import distance from '@turf/distance';
import { MapControl } from '@leverege/ui-mapbox-elements';
import Geohash from "../Geohash.js";
import { FilterSourcesModel } from "../../../../../filters/index.js";
import PromiseQueue from "../PromiseQueue.js";
import Util from "../Util.js";
import Model from "../GeohashGridModel.js";
const MAX_ZOOM = 22;
const MAX_PRECISION = 9;
const MIN_PRECISION = 1;
const MIN_BUCKET_SIZE = 3;
function getPrecisionFromZoom(zoom) {
  return Math.round(Math.min(zoom / MAX_ZOOM, 1) * (MAX_PRECISION - MIN_PRECISION) + MIN_PRECISION);
}
export default function useGeohashGridData(props) {
  const {
    attribute,
    delegate,
    filters,
    map,
    relationship,
    model,
    selected
  } = props;
  const [buckets, setBuckets] = useState([]);
  const [bounds, setBounds] = useState();
  const aggTimer = useRef();
  const dataTimer = useRef();
  const aggQueue = useRef(new PromiseQueue());
  const dataQueue = useRef(new PromiseQueue());
  const zoom = map.getZoom();
  const minBucketSize = useMemo(() => {
    if (zoom > 19) {
      return 200;
    }
    return MIN_BUCKET_SIZE;
  }, [zoom]);
  useEffect(() => {
    aggQueue.current.then(res => {
      setBuckets(res.aggs.geohash.buckets);
    });
    map.addListener('bounds_changed', () => {
      setBounds(map.getBounds().toJSON());
    });

    // const hook = ( ...args ) => {
    //   console.log( 'this is fun', args )
    // }

    // SelectionResolver.addHook( hook )

    // return () => {
    //   SelectionResolver.removeHook( hook )
    // }
  }, []);
  const hasSingleItems = useMemo(() => {
    return buckets.some(bucket => bucket.count <= minBucketSize);
  }, [buckets, minBucketSize]);
  const getAggregation = useCallback(() => {
    const zoom = map.getZoom();
    const b = bounds ? {
      top: bounds.north,
      left: bounds.west,
      right: bounds.east,
      bottom: bounds.south
    } : undefined;
    const precision = getPrecisionFromZoom(zoom);
    aggQueue.current.push(() => {
      return GlobalState.dispatch(delegate.search({
        limit: 0,
        filter: filters ? FilterSourcesModel.getFilter(filters) : null,
        aggs: {
          geohash: {
            type: 'geohashGrid',
            field: attribute.source.field,
            precision,
            bounds: b,
            aggs: {
              centroid: {
                type: 'geoCentroid',
                field: attribute.source.field
              }
            }
          }
        },
        queryName: `${delegate.queryName}.aggs`
      }));
    });
  }, [bounds, filters]);
  const getData = useCallback(() => {
    const singleBuckets = buckets.filter(bucket => bucket.count <= minBucketSize).map(bucket => bucket.key);
    let filter = null;
    if (singleBuckets.length > 0) {
      filter = {
        type: 'logical',
        operator: 'or',
        conditions: singleBuckets.map(bucket => ({
          type: 'geoGrid',
          field: attribute.source.field,
          geohash: bucket
        }))
      };
    }
    const pFilters = filters ? FilterSourcesModel.getFilter(filters) : null;
    if (pFilters) {
      if (!filter) {
        filter = pFilters;
      } else {
        filter = {
          type: 'logical',
          operator: 'and',
          conditions: [filter, pFilters]
        };
      }
    }
    dataQueue.current.push(() => {
      return GlobalState.dispatch(delegate.search({
        filter
      }));
    });
  }, [buckets, minBucketSize]);
  useEffect(() => {
    clearTimeout(dataTimer.current);
    dataTimer.current = setTimeout(() => {
      getData();
    }, 250);
  }, [getData]);
  useEffect(() => {
    clearTimeout(aggTimer.current);
    aggTimer.current = setTimeout(() => {
      getAggregation();
    }, 250);
  }, [getAggregation]);
  useEffect(() => {
    if (!selected?.items || selected.items.length === 0 || !selected.items[0].type.endsWith('-geohash')) {
      return;
    }
    const {
      sw,
      ne
    } = Geohash.bounds(selected.items[0].id);
    MapControl.flyToBounds('map', [[sw.lon, sw.lat], [ne.lon, ne.lat]], {
      padding: 60
    });
  }, [selected]);
  const centers = useMemo(() => {
    const options = {
      units: 'meters'
    };
    return buckets.filter(bucket => bucket.count > minBucketSize).map(bucket => {
      const id = bucket.key;
      const {
        bounds: {
          sw: {
            lon: minX,
            lat: minY
          },
          ne: {
            lon: maxX,
            lat: maxY
          }
        },
        lat,
        lon
      } = Geohash.decode(bucket.key);
      const maxSize = Math.min(distance(point([minX, minY]), point([minX, maxY]), options), distance(point([minX, minY]), point([maxX, minY]), options));
      const p = point([lon, lat], {
        count: bucket.count,
        id,
        dataId: id,
        dataType: `${relationship.objectType}-geohash`,
        dataRef: delegate.getInterfaceUrl(),
        hash: bucket.key,
        maxSize,
        fillColor: Util.getColor(Model.getFillColor(model), bucket),
        lineColor: Util.getColor(Model.getLineColor(model), bucket),
        labelColor: Util.getColor(Model.getLabelColor(model), bucket),
        opacity: Model.getFillOpacity(model),
        rolloverFillColor: Util.getColor(Model.getRolloverFillColor(model), bucket),
        rolloverLineColor: Util.getColor(Model.getRolloverLineColor(model), bucket),
        rolloverLabelColor: Util.getColor(Model.getRolloverLabelColor(model), bucket),
        rolloverOpacity: Model.getRolloverFillOpacity(model),
        labelSize: Model.getLabelSize(model)
      });
      return p;
    }).sort((a, b) => {
      if (a.properties.count < b.properties.count) {
        return -1;
      }
      return 1;
    });
  }, [buckets, model, minBucketSize]);
  return {
    centers,
    hasSingleItems
  };
}